import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, Download, Clock, CheckCircle, ArrowLeft, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

const TrustEstateEstablishment = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/diy-processes')}
            className="mb-8 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to DIY Processes
          </Button>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-xl bg-primary/10">
                <FileText className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Establish a Private Trust. Never Be Taxed again
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Learn how to establish private trusts and estates for asset protection and privacy. 
              Create legal structures that protect your wealth and property.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Time Estimate</h3>
              <p className="text-muted-foreground">1 day - 4 weeks</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Download className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Templates</h3>
              <p className="text-muted-foreground">4 templates included</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-8 mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground mb-6">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Essential Knowledge</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Trust vs estate structures and applications
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Asset protection strategies and methods
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Privacy and confidentiality protocols
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">Practical Skills</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Legal document preparation and execution
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Trustee selection and fiduciary duties
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Ongoing administration and compliance
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground underline">
              Start below
            </h2>
          </div>

          {/* Step Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Step 1 */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">1</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Understanding Trust Structures
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 1
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 1: Understanding Trust Structures
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-foreground">The War for Your Soul: Private Trust vs. Legal Fiction</h3>
                      
                      <div className="space-y-6">
                        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-bold text-red-800 dark:text-red-200">You Are Not Who They Say You Are</h4>
                          <p className="text-red-700 dark:text-red-300">
                            Every day, you rise and move through the world thinking you're free. You believe you own your property, that you control your destiny, and that you answer only to yourself.
                          </p>
                          <p className="text-red-700 dark:text-red-300 font-medium">
                            But you don't.
                          </p>
                          <p className="text-red-700 dark:text-red-300">
                            The proof? It's in your wallet.
                          </p>
                          <p className="text-red-700 dark:text-red-300">
                            That Social Security card, that driver's license, those W-2s and tax forms with your NAME IN ALL CAPS?
                          </p>
                          <p className="text-red-700 dark:text-red-300 font-bold">
                            Those aren't you. They're your leash.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">The Greatest Deception Ever Perpetrated</h4>
                          <p className="text-foreground">
                            The moment you were born, they created a legal fiction in your name. A corporate placeholder. A lifeless ens legis—Latin for "creature of the law."
                          </p>
                          <p className="text-foreground">
                            A dead thing, designed to contract with governments and corporations… while draining the living energy of you—the living soul.
                          </p>
                          <p className="text-foreground font-medium">
                            This entity isn't optional. It was imposed.
                          </p>
                          <p className="text-foreground">
                            And you've been carrying it ever since.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">What Is the Ens Legis?</h4>
                          <p className="text-foreground font-medium">
                            The ens legis is not you.
                          </p>
                          <p className="text-foreground">
                            It is a State-owned corporate vessel operating in commercial jurisdiction—built to extract, surveil, and control.
                          </p>
                          <p className="text-foreground">
                            Per Black's Law Dictionary (4th ed.):
                          </p>
                          <blockquote className="border-l-4 border-primary/50 pl-4 italic text-muted-foreground">
                            "An artificial being, created by law for the purposes of the law."
                          </blockquote>
                          <p className="text-foreground">That includes:</p>
                          <ul className="list-disc pl-6 space-y-1 text-foreground">
                            <li>The U.S. citizen</li>
                            <li>The ALL CAPS NAME</li>
                            <li>The Social Security Number holder</li>
                            <li>The defendant, the taxpayer, the person in commerce</li>
                          </ul>
                          <p className="text-foreground font-medium">
                            These are not who you are—they are who they need you to think you are.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">The Hidden Trust That Enslaves You</h4>
                          <p className="text-foreground">
                            This ens legis is part of a covert trust structure—a twisted inversion of lawful trust principles:
                          </p>
                          <div className="bg-background border border-border rounded p-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="font-semibold">Role</div>
                              <div className="font-semibold">Entity</div>
                              <div>Grantor</div>
                              <div>The State</div>
                              <div>Trustee</div>
                              <div>You (the living soul)</div>
                              <div>Trust Res</div>
                              <div>The ALL CAPS legal fiction</div>
                              <div>Beneficiary</div>
                              <div>Government agencies and banks</div>
                            </div>
                          </div>
                          <p className="text-foreground">
                            They made you the trustee of a vessel they created… and then made themselves the beneficiaries.
                          </p>
                          <div className="space-y-1 text-foreground">
                            <p>You assume all the risk.</p>
                            <p>You pay all the costs.</p>
                            <p>You accept all the rules.</p>
                          </div>
                          <p className="text-foreground font-medium">
                            They reap the benefits—while you live in bonded servitude… by legal design.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">The Government Doesn't Even Own the Land</h4>
                          <p className="text-foreground">
                            Here's the kicker:
                          </p>
                          <p className="text-foreground font-medium">
                            The United States government does not own America.
                          </p>
                          <p className="text-foreground">
                            It holds title to federal enclaves (e.g. D.C., military bases, national parks)—and even that control is based on contractual consent and limited delegation from the several states.
                          </p>
                          <p className="text-foreground">
                            Outside of those limited jurisdictions, the federal government is a guest—an operator—not a sovereign landlord.
                          </p>
                          <p className="text-foreground">
                            So what gives it power?
                          </p>
                          <p className="text-foreground font-medium">
                            Consent. Presumption. Contracts.
                            <br />
                            And your unaware participation as a trustee of their corporate fiction.
                          </p>
                        </div>

                        <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-bold text-green-800 dark:text-green-200">The Private Express Trust: The True Legal Equalizer</h4>
                          <p className="text-green-700 dark:text-green-300 font-medium">
                            This is where everything changes.
                          </p>
                          <p className="text-green-700 dark:text-green-300">
                            A Private Express Trust, built under natural law and common law contract, is not just outside of government jurisdiction—it outranks it.
                          </p>
                          <blockquote className="border-l-4 border-green-500/50 pl-4 italic text-green-600 dark:text-green-400">
                            "Whatever one may lawfully do himself, he may authorize another to do for him—without receiving any benefit or franchise from any government." - The Weiss Trustee Handbook
                          </blockquote>
                          <p className="text-green-700 dark:text-green-300">That means your trust:</p>
                          <ul className="list-disc pl-6 space-y-1 text-green-700 dark:text-green-300">
                            <li>Requires no charter</li>
                            <li>Is not a corporation</li>
                            <li>Exists by natural right, not legislative privilege</li>
                            <li>Is equal in standing, and in many respects, superior to any State-created entity</li>
                          </ul>
                          <p className="text-green-700 dark:text-green-300 font-bold">
                            You are no longer in their chain of command.
                            <br />
                            You are outside of their jurisdiction—not beneath it, but above it.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">The Corrected Trust Structure</h4>
                          <div className="bg-background border border-border rounded p-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="font-semibold">Role</div>
                              <div className="font-semibold">Entity</div>
                              <div>Grantor</div>
                              <div>You, the living soul</div>
                              <div>Trustee</div>
                              <div>Whomever you appoint (or yourself)</div>
                              <div>Trust Estate</div>
                              <div>Your land, labor, and property</div>
                              <div>Beneficiaries</div>
                              <div>Those you choose</div>
                            </div>
                          </div>
                          <p className="text-foreground font-medium">
                            This is not just administration. This is autonomy.
                            <br />
                            This is jurisdictional separation.
                            <br />
                            This is freedom with teeth.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">The Real Jurisdictional Battle</h4>
                          <p className="text-foreground">
                            This isn't political.
                            <br />
                            This isn't financial.
                            <br />
                            This is spiritual warfare waged through legal fictions.
                          </p>
                          
                          <div className="grid md:grid-cols-2 gap-4 mt-4">
                            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded p-3">
                              <h5 className="font-bold text-red-800 dark:text-red-200 mb-2">Their System: Legal Fiction</h5>
                              <ul className="text-sm space-y-1 text-red-700 dark:text-red-300">
                                <li><strong>Jurisdiction:</strong> Statutory / Admiralty / Commercial</li>
                                <li><strong>Status:</strong> Corporate entity / U.S. citizen</li>
                                <li><strong>Standing:</strong> Trustee of State property</li>
                                <li><strong>Privacy:</strong> None</li>
                                <li><strong>Control:</strong> Government agencies</li>
                                <li><strong>Law:</strong> Administrative code</li>
                                <li><strong>Rights:</strong> Civil privileges, revocable anytime</li>
                              </ul>
                            </div>
                            
                            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded p-3">
                              <h5 className="font-bold text-green-800 dark:text-green-200 mb-2">Your System: Private Express Trust</h5>
                              <ul className="text-sm space-y-1 text-green-700 dark:text-green-300">
                                <li><strong>Jurisdiction:</strong> Private / Common Law / Contract</li>
                                <li><strong>Status:</strong> Living man/woman, sui juris</li>
                                <li><strong>Standing:</strong> Creator of your trust, not subject of theirs</li>
                                <li><strong>Privacy:</strong> Absolute (protected under equity)</li>
                                <li><strong>Control:</strong> You and your trustees</li>
                                <li><strong>Law:</strong> Natural law + Contract law</li>
                                <li><strong>Rights:</strong> Unalienable and lawfully protected</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-bold text-primary">This Is About More Than Paperwork</h4>
                          <p className="text-foreground">
                            This is about your energy.
                            <br />
                            This is about your lineage.
                            <br />
                            This is about your legacy.
                          </p>
                          <p className="text-foreground">The ens legis allows them to:</p>
                          <ul className="list-disc pl-6 space-y-1 text-foreground">
                            <li>Tax your labor</li>
                            <li>Claim your property</li>
                            <li>Bind your contracts</li>
                            <li>Regulate your children</li>
                            <li>Police your movement</li>
                            <li>Decide your identity</li>
                          </ul>
                          <p className="text-foreground font-medium">
                            All because you unknowingly volunteered as trustee of a fiction.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">The Private Trust Is Your Legal Exodus</h4>
                          <p className="text-foreground">
                            When you form a properly constructed Private Express Trust, you are not evading their system—you are exiting it.
                          </p>
                          <p className="text-foreground">You're declaring:</p>
                          <blockquote className="border-l-4 border-primary/50 pl-4 italic text-foreground font-medium">
                            "I am not your legal fiction.
                            <br />
                            I am not a vessel in your jurisdiction.
                            <br />
                            I am a living man, a living woman.
                            <br />
                            I contract by right, not permission.
                            <br />
                            And this trust is my structure of lawful self-governance."
                          </blockquote>
                          <p className="text-foreground">
                            No charter.
                            <br />
                            No franchise.
                            <br />
                            No chains.
                          </p>
                        </div>

                        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">The True Freedom Structure</h4>
                          <p className="text-foreground">Unlike their commercial system, your Private Trust:</p>
                          <ul className="list-disc pl-6 space-y-1 text-foreground">
                            <li>Cannot be taxed without consent</li>
                            <li>Cannot be regulated by legislative code</li>
                            <li>Cannot be penetrated unless you allow it</li>
                            <li>Cannot be revoked by public policy</li>
                            <li>Is enforceable under the highest law: contract and equity</li>
                          </ul>
                          <p className="text-foreground">
                            Governments use trusts to rule.
                            <br />
                            Banks use trusts to hide and grow wealth.
                            <br />
                            You can use a trust to live free.
                          </p>
                          <p className="text-foreground font-medium">
                            Not because you're special.
                            <br />
                            Because you're lawful.
                          </p>
                        </div>

                        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-bold text-primary">The Choice Is Simple</h4>
                          <p className="text-foreground">
                            Keep living as their trustee, under their rules, for their benefit.
                            <br />
                            Or create your own trust, for your own life, on your own terms.
                          </p>
                          <p className="text-foreground">
                            This is not theory.
                            <br />
                            This is not loophole law.
                            <br />
                            This is foundational.
                          </p>
                        </div>

                        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                          <h4 className="text-lg font-bold text-primary">This Is the Higher Path</h4>
                          <p className="text-foreground">
                            This isn't about hiding.
                            <br />
                            It's about standing tall.
                          </p>
                          <p className="text-foreground">
                            This isn't about rebellion.
                            <br />
                            It's about restoration.
                          </p>
                          <p className="text-foreground">
                            This isn't about escaping the system.
                            <br />
                            It's about remembering who you are.
                          </p>
                          <p className="text-foreground font-bold">
                            The ens legis was designed for your bondage.
                            <br />
                            The trust is designed for your freedom.
                          </p>
                        </div>
                      </div>

                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Time for Action:</h4>
                        <p className="text-amber-700 dark:text-amber-300">
                          Reflect on the profound implications of legal fictions vs. private trusts. Consider how understanding this distinction can transform your relationship with legal and financial systems.
                        </p>
                      </div>

                      <div className="flex justify-end pt-4">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                              Go to Step 2
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                            <DialogHeader className="relative pb-4 border-b border-border/20">
                              <DialogClose asChild>
                                <Button
                                  variant="outline"
                                  className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                                >
                                  <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                                  <span className="text-sm font-semibold">Back</span>
                                </Button>
                              </DialogClose>
                              <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                                Step 2: Drafting Trust Documents
                              </DialogTitle>
                            </DialogHeader>
                            <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                              <div className="space-y-6 md:space-y-8">
                                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
                                  <p className="text-muted-foreground italic">Content will be available soon.</p>
                                </div>
                              </div>
                              <div className="h-16"></div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 2 */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">2</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Drafting Trust Documents
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 2
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 2: Drafting Trust Documents
                  </DialogTitle>
                </DialogHeader>
              <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                <div className="space-y-6 md:space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-foreground">Drafting Your Private Express Trust Documents</h3>
                    <h4 className="text-lg font-medium text-primary">Construct the Vessel of Your Jurisdiction</h4>
                    <p className="text-foreground">
                      This is your lawful act of creation—not of rebellion, but of separation. You are not building a legal tool.
                      You are forming a private republic, governed by contract, grounded in Natural Law, and immune from statutory entanglements.
                    </p>
                    <p className="text-foreground">
                      Below are the essential components and the exact steps to draft your Private Express Trust, ensuring it stands outside the public, beyond statutory reach, and under your own lawful authority.
                    </p>
                  </div>

                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                    <h4 className="text-lg font-semibold text-primary flex items-center">
                      🔑 Core Documents of a Private Trust
                    </h4>
                    <p className="text-foreground">You will need to draft the following:</p>
                    <ul className="list-disc pl-6 space-y-2 text-foreground">
                      <li><strong>Trust Indenture</strong> (or Trust Agreement) – the full private contract that governs the trust.</li>
                      <li><strong>Certificate of Trust</strong> – a simplified summary for external proof of existence (when required).</li>
                      <li><strong>Schedule A</strong> – the list of assets conveyed to the trust (the res).</li>
                      <li><strong>Beneficiary Declaration</strong> – identifies those who benefit under the trust.</li>
                      <li><strong>Trustee Acceptance & Appointment Letter</strong> – formal acceptance of trust duties.</li>
                      <li><strong>Minutes or Resolutions</strong> (optional) – record any decisions made under the trust.</li>
                      <li><strong>Private Administrative Instructions</strong> (optional) – for advanced organization or transitions.</li>
                    </ul>
                  </div>

                  <div className="space-y-6">
                    <h4 className="text-lg font-semibold text-primary flex items-center">
                      🧱 Step-by-Step Construction
                    </h4>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                      <h5 className="font-semibold text-foreground">Step 1: Name the Trust</h5>
                      <p className="text-foreground">Choose a name that reflects the spirit of sovereignty and clarity.</p>
                      <div className="bg-background border border-border rounded p-3">
                        <p className="text-sm font-medium text-foreground mb-2">Examples:</p>
                        <ul className="list-disc pl-4 space-y-1 text-sm text-foreground">
                          <li>The House of Ade Sovereign Trust</li>
                          <li>The Living Private Estate Trust</li>
                          <li>The Eternal Dominion Trust</li>
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Avoid using your full legal name. Avoid "revocable" or any indicators of statutory involvement.
                      </p>
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                      <h5 className="font-semibold text-foreground">Step 2: Draft the Trust Indenture</h5>
                      <p className="text-foreground">This is the master document, the private constitution of your trust. Include the following sections:</p>
                      
                      <div className="space-y-4">
                        <div className="bg-background border border-border rounded p-3">
                          <h6 className="font-medium text-foreground mb-2">🔹 Preamble</h6>
                          <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                            "This Private Express Trust is created this ___ day of ____________, by [Grantor Name], a living man/woman sui juris, for the exclusive purpose of holding, managing, and protecting property under Natural Law and private contract jurisdiction."
                          </blockquote>
                        </div>

                        <div className="bg-background border border-border rounded p-3">
                          <h6 className="font-medium text-foreground mb-2">🔹 Parties</h6>
                          <ul className="list-disc pl-4 space-y-1 text-sm text-foreground">
                            <li><strong>Grantor</strong> – You, the living man or woman who creates the trust.</li>
                            <li><strong>Trustee(s)</strong> – The individual(s) who agree to manage the trust.</li>
                            <li><strong>Beneficiaries</strong> – Those designated to benefit under the trust.</li>
                          </ul>
                        </div>

                        <div className="bg-background border border-border rounded p-3">
                          <h6 className="font-medium text-foreground mb-2">🔹 Purpose Clause</h6>
                          <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                            "This trust exists to exercise the natural right of contract, self-governance, and to hold property privately for the protection, prosperity, and legacy of the beneficiaries."
                          </blockquote>
                        </div>

                        <div className="bg-background border border-border rounded p-3">
                          <h6 className="font-medium text-foreground mb-2">🔹 Jurisdiction Clause</h6>
                          <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                            "This trust is formed under the private law of contract and governed by the common law and laws of nature, independent of statutory authority. No legislative body or court of equity shall have jurisdiction over this private agreement."
                          </blockquote>
                        </div>

                        <div className="bg-background border border-border rounded p-3">
                          <h6 className="font-medium text-foreground mb-2">🔹 Duties and Powers of the Trustee</h6>
                          <p className="text-sm text-foreground mb-2">Grant authority, but keep limitations clear:</p>
                          <ul className="list-disc pl-4 space-y-1 text-sm text-foreground">
                            <li>Duty of loyalty</li>
                            <li>No commingling of property</li>
                            <li>Record-keeping (optional)</li>
                            <li>Power to enter contracts on behalf of the trust</li>
                          </ul>
                        </div>

                        <div className="bg-background border border-border rounded p-3">
                          <h6 className="font-medium text-foreground mb-2">🔹 Duration Clause</h6>
                          <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                            "This trust shall remain in effect in perpetuity, or until lawfully dissolved by its terms."
                          </blockquote>
                        </div>

                        <div className="bg-background border border-border rounded p-3">
                          <h6 className="font-medium text-foreground mb-2">🔹 Non-Statutory Disclaimer</h6>
                          <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                            "This trust is not registered with any State agency, nor is it created pursuant to any public statute. It is a private entity formed by the inalienable right to contract."
                          </blockquote>
                        </div>
                      </div>
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                      <h5 className="font-semibold text-foreground">Step 3: Draft the Certificate of Trust</h5>
                      <p className="text-foreground">This is a one-page document you can use to show proof of existence without disclosing trust terms.</p>
                      <div className="bg-background border border-border rounded p-3">
                        <p className="text-sm font-medium text-foreground mb-2">Include:</p>
                        <ul className="list-disc pl-4 space-y-1 text-sm text-foreground">
                          <li>Name of the trust</li>
                          <li>Date of creation</li>
                          <li>Name of trustee</li>
                          <li>Signature of grantor and trustee</li>
                          <li>Notary (optional but helpful for evidentiary use)</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                      <h5 className="font-semibold text-foreground">Step 4: Create Schedule A – Declaration of Res</h5>
                      <p className="text-foreground">This is a private ledger showing what has been placed into the trust.</p>
                      <div className="bg-background border border-border rounded p-3">
                        <p className="text-sm font-medium text-foreground mb-2">Example:</p>
                        <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                          "On this day, I hereby transfer and convey the following property into The Eternal Dominion Trust:
                          <br />• Real estate at [address]
                          <br />• All intellectual property held in my name
                          <br />• 100% interest in [business name]
                          <br />• Personal property as listed in Attachment A"
                        </blockquote>
                      </div>
                      <p className="text-sm text-muted-foreground">You do not file this document publicly.</p>
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                      <h5 className="font-semibold text-foreground">Step 5: Declare the Beneficiaries</h5>
                      <p className="text-foreground">List the beneficiaries in a separate document or within the indenture.</p>
                      <div className="bg-background border border-border rounded p-3">
                        <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                          "The following living men/women shall benefit from this trust:
                          <br />• [Full name, with relationship, e.g., 'my daughter']
                          <br />• [Or class-based: 'The lawful heirs of the grantor, to be named later']"
                        </blockquote>
                      </div>
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                      <h5 className="font-semibold text-foreground">Step 6: Trustee Acceptance</h5>
                      <p className="text-foreground">This is a short written agreement where the trustee accepts the duties and responsibilities.</p>
                      <div className="bg-background border border-border rounded p-3">
                        <blockquote className="text-sm italic text-muted-foreground border-l-2 border-primary/50 pl-3">
                          "I, [Trustee Name], a living man/woman, do hereby accept the duties as trustee of The Living Private Estate Trust, and agree to carry out the lawful instructions under private contract, with good faith and fiduciary duty."
                        </blockquote>
                        <p className="text-sm text-foreground mt-2">Signed and dated by the trustee.</p>
                      </div>
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
                      <h5 className="font-semibold text-foreground">Step 7 (Optional but Wise): Create Administrative Tools</h5>
                      <ul className="list-disc pl-4 space-y-1 text-foreground">
                        <li><strong>Trust Minutes or Resolutions:</strong> Record major actions taken (e.g., sale of a property).</li>
                        <li><strong>Private Administrative Instructions:</strong> Internal guidance for transitions or successor trustees.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                    <h4 className="text-lg font-semibold text-primary flex items-center">
                      🏛️ Final Notes on Execution and Maintenance
                    </h4>
                    <ul className="space-y-2 text-foreground">
                      <li><strong>Notarization:</strong> Not required by law, but recommended for evidentiary strength.</li>
                      <li><strong>Recording:</strong> DO NOT record the indenture. You may optionally record the Certificate of Trust if you need to interact with financial institutions.</li>
                      <li><strong>Language:</strong> Always refer to parties as "living man" or "woman sui juris." Avoid engaging legal titles (Mr., Ms., etc.).</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                    <h4 className="text-lg font-bold text-primary flex items-center">
                      📜 What You've Just Created
                    </h4>
                    <p className="text-foreground font-medium">
                      This isn't just a legal structure.
                      <br />
                      This is your vessel, your interface, your private jurisdictional home.
                    </p>
                    <p className="text-foreground">Your trust is:</p>
                    <ul className="list-disc pl-6 space-y-1 text-foreground">
                      <li>A firewall between you and legal fictions</li>
                      <li>A declaration of self-governance</li>
                      <li>A structure to engage with the world without becoming of it</li>
                      <li>A tool to administer your affairs without re-entering their system</li>
                    </ul>
                    <p className="text-foreground font-medium">
                      You've just laid the foundation for true autonomy, not through protest, but through peaceful private contract—the highest law on Earth.
                    </p>
                  </div>
                </div>
                <div className="h-16"></div>
              </div>
              </DialogContent>
            </Dialog>

            {/* Step 3 */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">3</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Executing & Recording
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 3
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 3: Executing & Recording
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-foreground flex items-center">
                        <span className="text-2xl mr-2">⚖️</span>
                        Execution & Recording Instruction Set
                      </h3>
                      
                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground">Bringing Declarations, Affidavits, and Private Trusts into Effect through Lawful Notice</h4>
                        <blockquote className="border-l-4 border-primary/50 pl-4 italic text-muted-foreground">
                          "A claim without enforcement is a whisper. A right unrecorded is a presumption waived."
                        </blockquote>
                        
                        <p className="text-foreground">
                          You've created the core instruments of sovereignty:
                        </p>
                        <ul className="list-disc pl-6 space-y-1 text-foreground">
                          <li>A Declaration of who and what you are</li>
                          <li>Affidavits of truth and standing</li>
                          <li>A Private Express Trust to act as your lawful transmitting utility</li>
                          <li>Notices to rebut presumptions and clarify your private status</li>
                        </ul>
                        
                        <p className="text-foreground font-medium">
                          Now it's time to make it real in the world—by giving lawful notice, establishing public record, and executing these documents with intentional authority.
                        </p>
                      </div>

                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">🧩</span>
                          What to Execute and/or Record
                        </h4>
                        <p className="text-foreground">These are the documents to prepare for finalization:</p>
                        
                        <div className="grid grid-cols-1 gap-2 text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✅</span>
                            <span><strong>Declaration of Status</strong> – A spiritual and lawful expression of natural standing</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✅</span>
                            <span><strong>Affidavit(s)</strong> – Statements of fact under oath, rebutting legal presumptions</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✅</span>
                            <span><strong>Notice of Status Correction</strong> – Ending any claim of citizenship or corporate franchise</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✅</span>
                            <span><strong>Trust Certificate</strong> – A summary document proving existence of your private trust</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✅</span>
                            <span><strong>Trustee Acceptance</strong> – Confirmation of acceptance of trustee role</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✅</span>
                            <span><strong>(Optional) Schedule of Assets (Schedule A)</strong> – What the trust holds</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✅</span>
                            <span><strong>(Optional) Public Notice Template</strong> – One-pager that announces status to the world</span>
                          </div>
                        </div>
                        
                        <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded p-3">
                          <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                            <strong>Note:</strong> Trust indentures, private contracts, and asset ledgers should not be recorded publicly. They remain private and sacred.
                          </p>
                        </div>
                      </div>

                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">✍️</span>
                          Step 1: Sign with Purpose and Authority
                        </h4>
                        <ul className="list-disc pl-6 space-y-2 text-foreground">
                          <li>Use blue ink to indicate living original intent</li>
                          <li>Sign using non-corporate, non-legalistic format<br />
                            <em>Example: By: a living man / woman or Authorized Representative of Private Trust</em></li>
                          <li>Avoid including ZIP codes, Social Security Numbers, or STATE abbreviations when possible</li>
                        </ul>
                        <p className="text-foreground font-medium">
                          This is not about hiding—it's about not voluntarily stepping into their jurisdiction.
                        </p>
                      </div>

                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">🔏</span>
                          Step 2: Notarize Strategic Documents
                        </h4>
                        <p className="text-foreground">
                          While private law does not require notarization, it adds weight as unrebutted evidence and may protect your position in administrative settings.
                        </p>
                        
                        <div className="space-y-2">
                          <p className="font-medium">Recommended documents to notarize:</p>
                          <ul className="list-disc pl-6 space-y-1 text-foreground">
                            <li>Affidavit(s) of Truth or Status</li>
                            <li>Declaration of Status</li>
                            <li>Certificate of Trust</li>
                            <li>Trustee Acceptance Form</li>
                            <li>Notice of Status Correction</li>
                          </ul>
                        </div>
                        
                        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                          <p className="text-blue-800 dark:text-blue-200 text-sm">
                            <strong>Let the notary know:</strong> "This is a private document signed in private capacity, not in a corporate or statutory role."
                          </p>
                        </div>
                      </div>

                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">🗃️</span>
                          Step 3: Record at the County Clerk's Office
                        </h4>
                        <p className="text-foreground">
                          Recording gives constructive notice to the world and solidifies your position.
                        </p>
                        
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium">Visit any County Clerk and Recorder's Office within any U.S. county. Record the following:</p>
                            <ul className="list-disc pl-6 space-y-1 text-foreground mt-2">
                              <li>Declaration of Status</li>
                              <li>Affidavit(s) of Truth or Fact</li>
                              <li>Notice of Status Correction</li>
                              <li>Certificate of Trust</li>
                            </ul>
                          </div>
                          
                          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded p-3">
                            <p className="text-green-800 dark:text-green-200 text-sm">
                              <strong>You may say:</strong> "I am submitting these documents for lawful public notice and recording under common law. This is not a UCC filing or under statutory law."
                            </p>
                          </div>
                          
                          <p className="text-foreground">
                            Bring two originals: one for them to record, one to be stamped and returned to you.
                          </p>
                          
                          <p className="text-foreground">
                            <span className="text-green-600">✅</span> <strong>Optional:</strong> Some counties allow digital recording—check their website.
                          </p>
                        </div>
                      </div>

                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">🌐</span>
                          Step 4: Establish a Public Posting Location
                        </h4>
                        <p className="text-foreground">
                          To strengthen the public record and create accessible notice:
                        </p>
                        
                        <div className="space-y-3">
                          <div>
                            <p className="font-medium">Create a free or private-hosted website (or use Google Drive/Dropbox with shareable links)</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Label it something like: "Lawful Public Notices – Private Status, Declarations, and Trust Records"
                            </p>
                          </div>
                          
                          <div>
                            <p className="font-medium">Upload:</p>
                            <ul className="list-disc pl-6 space-y-1 text-foreground">
                              <li>PDF versions of recorded documents</li>
                              <li>Scanned notary seals (optional)</li>
                              <li>A cover statement declaring lawful purpose and private jurisdiction</li>
                            </ul>
                          </div>
                          
                          <div className="bg-primary/10 border border-primary/20 rounded p-3">
                            <p className="text-primary font-medium text-sm">
                              You are not hiding. You are declaring jurisdiction and protecting it through visibility.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">📨</span>
                          Step 5 (Optional): Serve Copies to Interested Parties
                        </h4>
                        <p className="text-foreground">
                          To terminate presumptions or ensure enforcement, you may send copies via Certified Mail to:
                        </p>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <ul className="list-disc pl-6 space-y-1 text-foreground">
                            <li>IRS</li>
                            <li>SSA</li>
                            <li>U.S. State Department</li>
                          </ul>
                          <ul className="list-disc pl-6 space-y-1 text-foreground">
                            <li>U.S. Department of Justice</li>
                            <li>Local courts or agencies presuming jurisdiction</li>
                          </ul>
                        </div>
                        
                        <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded p-3">
                          <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                            <strong>Enclose a cover page:</strong> "This is a lawful notice of private status and correction of public presumption. Please file this under administrative record. This does not request adjudication—it is notice and demand for correction."
                          </p>
                        </div>
                        
                        <p className="text-foreground text-sm">
                          Always keep receipts, mailing labels, and notarized affidavits of service if possible.
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">🔚</span>
                          Final Notice to the World
                        </h4>
                        <blockquote className="border-l-4 border-primary/50 pl-4 italic text-foreground">
                          Let it be known: I am not lost at sea. I am not an artificial entity, franchise, or fiction. I stand in my full capacity as a living man/woman under God's law, and I operate through a lawfully created private trust under contract and natural jurisdiction. All declarations, affidavits, and notices issued herein are lawfully executed and publicly recorded. Any further presumptions against this status are null, void, and rebutted in total.
                        </blockquote>
                      </div>

                      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">✅</span>
                          Checklist: Final Review
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-border rounded">
                            <thead>
                              <tr className="border-b border-border bg-muted/50">
                                <th className="text-left p-2 font-medium">Document</th>
                                <th className="text-center p-2 font-medium">Sign</th>
                                <th className="text-center p-2 font-medium">Notarize</th>
                                <th className="text-center p-2 font-medium">Record</th>
                                <th className="text-center p-2 font-medium">Post Online</th>
                                <th className="text-center p-2 font-medium">Mail Copy</th>
                              </tr>
                            </thead>
                            <tbody className="text-xs">
                              <tr className="border-b border-border">
                                <td className="p-2">Declaration of Status</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2">Optional</td>
                              </tr>
                              <tr className="border-b border-border">
                                <td className="p-2">Affidavit(s) of Truth</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2">Optional</td>
                              </tr>
                              <tr className="border-b border-border">
                                <td className="p-2">Notice of Status Correction</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2">Recommended</td>
                              </tr>
                              <tr className="border-b border-border">
                                <td className="p-2">Certificate of Trust</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2">Optional</td>
                              </tr>
                              <tr className="border-b border-border">
                                <td className="p-2">Trustee Acceptance Letter</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-red-600">❌</td>
                                <td className="text-center p-2 text-red-600">❌</td>
                                <td className="text-center p-2 text-red-600">❌</td>
                              </tr>
                              <tr>
                                <td className="p-2">Trust Indenture (Private)</td>
                                <td className="text-center p-2 text-green-600">✅</td>
                                <td className="text-center p-2 text-red-600">❌</td>
                                <td className="text-center p-2 text-red-600">❌</td>
                                <td className="text-center p-2 text-red-600">❌</td>
                                <td className="text-center p-2 text-red-600">❌</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                        <h4 className="text-lg font-semibold text-foreground flex items-center">
                          <span className="text-xl mr-2">🛡️</span>
                          Conclusion: From Words to Jurisdiction
                        </h4>
                        <p className="text-foreground">
                          This is how lawful status is made visible.
                          <br />
                          Not through protest.
                          <br />
                          Not through registration.
                          <br />
                          But through private execution, public notice, and contractual clarity.
                        </p>
                        
                        <div className="space-y-2">
                          <p className="text-foreground">You've:</p>
                          <ul className="list-disc pl-6 space-y-1 text-foreground">
                            <li>Declared your intent</li>
                            <li>Corrected the record</li>
                            <li>Built your private vessel (the trust)</li>
                            <li>Rebutted the fiction (ens legis)</li>
                            <li>Planted your flag in jurisdictional stone</li>
                          </ul>
                        </div>
                        
                        <p className="text-foreground font-bold">
                          This is the practice of real freedom. This is how you stand.
                        </p>
                        
                        <div className="bg-background border border-border rounded p-3 space-y-2">
                          <p className="text-foreground font-medium">Let me know if you want:</p>
                          <ul className="list-none space-y-1 text-foreground text-sm">
                            <li><span className="text-green-600">✅</span> Templates for any document above</li>
                            <li><span className="text-green-600">✅</span> A downloadable public notice template</li>
                            <li><span className="text-green-600">✅</span> A web-ready notice page you can copy and publish</li>
                          </ul>
                          <p className="text-primary font-bold text-sm mt-3">
                            We move now with precision and power.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Step 4 */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 min-h-[160px] flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">4</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Ongoing Administration
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-4 group-hover:border-primary/50 transition-colors">
                      Start Step 4
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 4: Ongoing Administration
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <h3 className="text-xl font-semibold text-foreground">🧭 Ongoing Administration Instruction Set</h3>
                    
                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
                      <p className="text-foreground italic font-medium">
                        "The trust lives because you administer it. The moment you abandon it, the fiction returns."
                      </p>
                      <p className="text-foreground">
                        Creating the trust is the beginning. Maintaining it—lawfully, diligently, and in line with Natural Law and private contract—is how you keep your vessel afloat and fully out of the jurisdiction of the public.
                      </p>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
                      <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200">According to The Weiss Trustee Handbook:</h4>
                      <blockquote className="border-l-4 border-blue-500/50 pl-4 italic text-blue-700 dark:text-blue-300">
                        "The trust must be treated as a living, breathing legal person... distinct from the trustee and grantor. The trustee must think, act, and speak on behalf of the trust entity—not as themselves."
                      </blockquote>
                      <p className="text-blue-700 dark:text-blue-300">
                        This is the ongoing art of administration: keeping the trust in motion, distinct, and lawfully alive.
                      </p>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">⚖️ I. Core Principle: Separation of Personae</h4>
                      <p className="text-foreground">
                        As trustee (or grantor with administrative rights), you must never blur the lines between the trust and the natural person. All actions must be:
                      </p>
                      <ul className="list-disc pl-6 space-y-2 text-foreground">
                        <li>Taken in the name of the trust</li>
                        <li>Signed on behalf of the trust, not in individual capacity</li>
                        <li>Conducted with proper documentation and paper trails</li>
                      </ul>
                      <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <p className="text-amber-700 dark:text-amber-300 font-medium">
                          💡 Think of the trust as a private jurisdictional interface—a vessel. You navigate the public waters through this vessel, not by jumping overboard into their fiction.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">🗂️ II. Maintain Proper Records</h4>
                      <p className="text-foreground">
                        Your trust is private, but not careless. Its administration must be well-documented, even if it never sees a courtroom.
                      </p>
                      <p className="text-foreground font-medium">
                        Keep the following records in a secure, private location:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          "Copy of the Original Trust Indenture",
                          "Schedule A (Updated Asset Ledger)",
                          "Trustee Appointment & Acceptance Letter(s)",
                          "Minutes or Resolutions of Major Decisions",
                          "All Receipts, Contracts, or Agreements in the Trust's Name",
                          "Banking, Crypto, or Vault Transaction Logs (if applicable)",
                          "Tax-Related Correspondence (if you choose to file or rebut filings)",
                          "Trust Journal (optional) – Chronological notes of all trust activities"
                        ].map((item, index) => (
                          <div key={index} className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                            <span className="text-foreground text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                      <div className="bg-muted/50 border border-border rounded-lg p-4">
                        <p className="text-foreground italic">
                          From The Trustee Handbook: "The trustee must keep complete and accurate books, not for tax purposes but to prove and demonstrate lawful administration of the trust."
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">🧾 III. Conduct Trust Transactions Properly</h4>
                      <p className="text-foreground">
                        Whether you're buying, leasing, contracting, or communicating:
                      </p>
                      <ul className="list-disc pl-6 space-y-2 text-foreground">
                        <li>Always act as trustee, never personally</li>
                        <li>Use trust titles and signature lines like: <span className="font-mono bg-muted px-1 rounded">John Doe, Trustee for the Dominion Trust</span></li>
                        <li>All income, receipts, and bills must be issued in the trust's name</li>
                        <li>Use trust checks, trust crypto wallets, or trust bank accounts</li>
                      </ul>
                      <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-red-700 dark:text-red-300 font-medium">
                          You are never to commingle personal and trust affairs. Even one misstep could invite presumed jurisdiction or liability.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">🧑‍⚖️ IV. Record Resolutions for Key Events</h4>
                      <p className="text-foreground">
                        Every time the trust:
                      </p>
                      <ul className="list-disc pl-6 space-y-1 text-foreground">
                        <li>Acquires a new asset</li>
                        <li>Appoints or removes a trustee</li>
                        <li>Enters a contract</li>
                        <li>Makes a major disbursement</li>
                        <li>Sends legal correspondence</li>
                      </ul>
                      <p className="text-foreground">
                        ...you should create a resolution or trust minute that records the event.
                      </p>
                      <div className="bg-muted/50 border border-border rounded-lg p-4">
                        <h5 className="font-semibold text-foreground mb-2">Sample format:</h5>
                        <div className="bg-background border border-border rounded p-3 font-mono text-sm">
                          <p>On this ___ day of ________, the trustee(s) of the [Name of Trust] did resolve to acquire the following property: [description], for the benefit of the trust. This decision is in line with the trust's stated purpose and fiduciary duties. Signed, [Trustee Name]</p>
                        </div>
                        <p className="text-muted-foreground text-sm mt-2">Store these internally. Do not publish them.</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">🌐 V. Interfacing with the Public World</h4>
                      <p className="text-foreground">
                        Sometimes, the trust must deal with public agencies or banks. When doing so:
                      </p>
                      <ul className="list-disc pl-6 space-y-2 text-foreground">
                        <li>Use the Certificate of Trust only—never the indenture</li>
                        <li>Be clear: "This is a private express trust formed under common law and private contract jurisdiction. I am the trustee and authorized signatory."</li>
                        <li>Avoid filling out forms that list "trustee SSN" or "grantor address" tied to personal capacity. Rebut jurisdiction or use "Not Applicable."</li>
                      </ul>
                      <div className="bg-muted/50 border border-border rounded-lg p-4">
                        <p className="text-foreground italic">
                          From The Trustee Handbook: "A trustee may interface with the public world without submitting to its jurisdiction, so long as they do not voluntarily re-enter it."
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">🛡️ VI. Enforce Your Jurisdiction When Challenged</h4>
                      <p className="text-foreground">
                        If your trust is ever questioned:
                      </p>
                      <ul className="list-disc pl-6 space-y-2 text-foreground">
                        <li>Provide the Certificate of Trust, not the indenture</li>
                        <li>Respond with an Affidavit of Trust Existence & Private Capacity</li>
                        <li>Reference Article I, Section 10 of the Constitution—no law impairing the obligation of contract</li>
                        <li>State that the trust is a private contractual relationship under common law and is not subject to administrative code unless voluntarily consented to</li>
                      </ul>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">🔁 VII. Annual Trust Maintenance Ritual (Optional but Wise)</h4>
                      <p className="text-foreground">
                        Once a year, schedule a Trust Administration Review where you:
                      </p>
                      <ul className="list-disc pl-6 space-y-2 text-foreground">
                        <li>Update the Schedule A (assets)</li>
                        <li>Review trustee duties and succession</li>
                        <li>Update any minutes, resolutions, or changes</li>
                        <li>Print and bind current trust journal logs</li>
                        <li>Ensure your public notice repository is current</li>
                      </ul>
                      <p className="text-foreground">
                        This practice keeps your vessel clean, clear, and fully distinguishable from any legal fiction.
                      </p>
                    </div>

                    <div className="space-y-6">
                      <h4 className="text-lg font-semibold text-foreground">🚫 VIII. What Not to Do</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          "Don't treat the trust as a \"bank account\" for yourself",
                          "Don't co-mingle assets or receive checks in your name for trust business",
                          "Don't claim personal ownership of trust property",
                          "Don't file IRS forms unless it aligns with your status correction strategy",
                          "Don't act in both trustee and beneficiary roles unless legally structured to allow it"
                        ].map((item, index) => (
                          <div key={index} className="flex items-start">
                            <span className="text-red-500 mr-2 mt-1 text-lg">❌</span>
                            <span className="text-foreground text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-6 space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">🧱 Final Word: Maintain the Wall Between Worlds</h4>
                      <p className="text-foreground">
                        The trust is your citadel.<br />
                        It is not you, but it serves you.<br />
                        It is your lawful vessel, your administrative interface, and your shield from the fiction known as the ens legis system.
                      </p>
                      <div className="bg-muted/50 border border-border rounded-lg p-4">
                        <p className="text-foreground italic">
                          To quote The Trustee Handbook again: "A properly administered trust can be more powerful than any license, registration, or citizenship status. But it requires clarity, diligence, and loyalty to the purpose for which it was formed."
                        </p>
                      </div>
                      <p className="text-foreground font-medium">
                        So remain diligent. Act only in proper capacity. Keep records. Separate jurisdictions. Stay private. Stay sovereign.
                      </p>
                    </div>
                  </div>
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default TrustEstateEstablishment;